import React from 'react';

import CSSModules from './index.module.less';

import { rem } from '@helpers/Constants';
import Compatibility from '@helpers/Compatibility';
import envKeys from './envKeys';

import { IProps, IState } from './index.d';

export interface IDragRefresher {
  complete: () => void;
}

class DragRefresher extends React.PureComponent<IProps, IState> implements IDragRefresher {
  componentHeight = Math.ceil(rem * 0.88);
  draggerClassName = ' ' + CSSModules.dragger;
  state = {
    canExecute: false,
    canRefresh: false
  };

  //下拉刷新组件容器元素
  dragRefresher: React.RefObject<HTMLDivElement> = React.createRef();
  //滚动元素(必须具有滚动属性，否则无法正常操作)
  scroller?: HTMLElement | null;
  //被拖拽的元素
  dragger?: HTMLElement | null;

  startValue?: number;
  isRefreshing?: boolean;
  isCalculating?: boolean;
  hasTriggered?: boolean;

  componentDidMount(): void {
    const element = this.dragRefresher.current;
    if(element) {
      const { scrollerId, draggerId } = this.props;
      this.scroller = scrollerId ? document.getElementById(scrollerId) : element[envKeys.parent];
      if(this.scroller) {
        this.scroller.addEventListener('touchstart', this.handleTouchStart, false);
        this.scroller.addEventListener('touchmove', this.handleTouchMove, false);
        this.scroller.addEventListener('touchend', this.handleTouchEnd, false);
        this.dragger = draggerId ? document.getElementById(draggerId) : this.scroller;
      }
    }
  }

  componentWillUnmount(): void {
    if(this.scroller) {
      this.scroller.removeEventListener('touchstart', this.handleTouchStart, false);
      this.scroller.removeEventListener('touchmove', this.handleTouchMove, false);
      this.scroller.removeEventListener('touchend', this.handleTouchEnd, false);
    }
  }

  handleTouchStart = (event: TouchEvent): void => {
    if((this.scroller as HTMLElement).scrollTop <= 0) {
      //滚动条位于顶部才记录起始位置数值
      this.startValue = event[envKeys.touches][0][envKeys.y];
    } else {
      delete this.startValue;
    }
  }

  handleTouchMove = (event: TouchEvent): void => {
    if(typeof this.startValue === 'undefined') {
      return;
    }

    if(this.isRefreshing) {
      event.preventDefault();
      return;
    }

    let distance = event[envKeys.touches][0][envKeys.y] - this.startValue;
    distance = Math.ceil(distance * 0.3); //乘以0.3是为了让translateY数值避免过大
    if(distance < 0) {
      return;
    }

    event.preventDefault();
    if (this.isCalculating) {
      return;
    }
    this.isCalculating = true;
    this.hasTriggered = true;

    window.requestAnimationFrame(() => {
      if(this.dragger) {
        this.dragger.style[Compatibility.css.transform] = `translate3d(0px, ${ distance }px, 0px)`;
      }

      const overHeight = distance >= this.componentHeight;
      if(overHeight && !this.state.canRefresh) {
        this.setState({ canRefresh: true });
      } else if(!overHeight && this.state.canRefresh) {
        this.setState({ canRefresh: false });
      }
      delete this.isCalculating;
    });
  }

  handleTouchEnd = (): void => {
    if(!this.hasTriggered || this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    (this.dragger as HTMLElement).className += this.draggerClassName;

    window.requestAnimationFrame(() => {
      if(this.state.canRefresh) {
        (this.dragger as HTMLElement).style[Compatibility.css.transform] = 'translate3d(0px, .88rem, 0px)';
        this.setState({ canExecute: true });
        this.props.onRefresh();
      } else {
        this.complete();
      }
    });
  }

  complete = (): void => {
    (this.dragger as HTMLElement).style[Compatibility.css.transform] = '';
    //设置200毫秒延迟是由于父元素有一个0.2秒的过渡动画
    setTimeout(() => {
      delete this.hasTriggered;
      delete this.isRefreshing;
      delete this.startValue;
      this.setState({
        canRefresh: false,
        canExecute: false
      });

      if(this.dragger) {
        const className = this.dragger.className.replace(this.draggerClassName, '');
        this.dragger.className = className;
      }
    }, 200);
  }

  render(): JSX.Element {
    const {
      state: {
        canExecute,
        canRefresh
      },
      props: {
        className,
        loadingText,
        draggingText
      }
    } = this;
    const refresherExtraClass = className ? ' ' + className : '';
    const iconExtraClass = canRefresh ? ' ' + CSSModules.rotate : '';
    return (
      <div
        className={ 'flex flex-center' + refresherExtraClass }
        styleName="refresher"
        ref={ this.dragRefresher }
      >
        {
          canExecute ? (
            <i className="seewo-loading infinite-rotate" />
          ) : (
            <React.Fragment>
              <i className={ 'iconfont icon-arrow-down ' + iconExtraClass } styleName="transition" />
              <span>{ canRefresh ? loadingText : draggingText }</span>
            </React.Fragment>
          )
        }
      </div>
    );
  }

  static defaultProps = {
    className: '', //样式类名
    draggingText: '下拉即可刷新...', //拉动时显示文字
    loadingText: '释放即可刷新...', //加载时显示文字
    onRefresh: () => {} //刷新方法
  }
}

export default DragRefresher
